// HotX Console 0.1.101 / 2026-09-15 15:49 CST / parent af614734c5dbb3d86cfffddeb9474d6280d51092 / CDN-PACKAGE-SUBMIT-GUARD-20260915.
/**
 * HotX Console 购买套餐独立分区模型与选择规则。
 * 功能版本：Console 0.1.100；实际修改时间：2026-09-15 03:22:31 CST；功能源码基线：a9b8b6110efa5c3852b4719e25a0d3d45e99d5f6。
 */
// Console 0.1.101 / 2026-09-15 14:20 CST / parent fbbf070c7ba419eddc96bb73ea3afc37370db1d1.
// CDN-PACKAGE-CHECKOUT-20260915: 方案 B 直接消费真实分区额度，前端不预占也不伪造额度。
export type DomainPlanQuotaMode = 'TOTAL_DOMAIN' | 'ROOT_DOMAIN' | 'UNLIMITED';
export type DomainPlanPartitionStatus = 'ACTIVE' | 'PAYMENT_PAUSED' | 'EXPIRED' | 'CLOSED';

export interface DomainPlanPartitionCatalogItem {
  id: number;
  planCode: string;
  planName: string;
  pointPrice: number;
  periodMode?: string;
  quotaMode?: DomainPlanQuotaMode;
  quotaLimit?: number | null;
  autoRenewDefault?: boolean;
}

export interface DomainPlanPartition {
  id: number;
  planId: number;
  purchaseSequence: number;
  displayName?: string;
  planCode: string;
  planName: string;
  quotaMode: DomainPlanQuotaMode;
  quotaLimit: number | null;
  quotaUsed: number;
  pointPrice: number;
  periodMode: string;
  periodCount?: number;
  status: DomainPlanPartitionStatus;
  autoRenew: boolean;
  startAt: string;
  expireAt: string;
  protectionUntil?: string | null;
  manualResumeRequired: boolean;
}

export interface DomainPlanPartitionDomain {
  domainId: number;
  domain?: string;
  domainName?: string;
  partitionId?: number | null;
  partitionDisplayName?: string | null;
  allocationKey?: string | null;
  status: string;
}

export interface DomainPlanPartitionOverview {
  plans: DomainPlanPartitionCatalogItem[];
  partitions?: DomainPlanPartition[];
  domains: DomainPlanPartitionDomain[];
  serverTime: string;
  serverNow?: string;
}

export type DomainPackageSelection =
  | { mode: 'EXISTING'; partitionId: number }
  | {
      mode: 'NEW';
      planId: number;
      periodCount: number;
      autoRenew: boolean;
      purchaseConfirmed: true;
    };

export interface DomainPlanPartitionOption {
  key: string;
  id: number | null;
  planId: number;
  planCode: string;
  planName: string;
  pointPrice: number;
  quotaMode: DomainPlanQuotaMode;
  quotaLimit: number | null;
  quotaUsed: number;
  label: string;
  disabled: boolean;
  unavailableReason?: string;
  isNew: boolean;
  selection: DomainPackageSelection;
  partition?: DomainPlanPartition;
}

export type DomainPlanQuotaTone = 'available' | 'warning' | 'full' | 'muted';

export interface DomainPlanPartitionRow extends DomainPlanPartitionOption {
  quotaLabel: string;
  quotaTone: DomainPlanQuotaTone;
  postSubmitQuotaLabel: string;
}

function sequenceLabel(sequence: number) {
  let value = Math.max(1, Math.trunc(sequence));
  let label = '';
  while (value > 0) {
    value -= 1;
    label = String.fromCharCode(65 + (value % 26)) + label;
    value = Math.floor(value / 26);
  }
  return label;
}

export function partitionDisplayName(partition: DomainPlanPartition) {
  return `${partition.planName}${sequenceLabel(partition.purchaseSequence)}`;
}

export function partitionQuotaLabel(partition: Pick<DomainPlanPartition, 'quotaLimit' | 'quotaUsed'>) {
  return partition.quotaLimit === null
    ? `域名${partition.quotaUsed}/不限量`
    : `域名${partition.quotaUsed}/${partition.quotaLimit}`;
}

export function partitionOptions(
  overview: DomainPlanPartitionOverview | null | undefined,
  selectedPlanId?: number
): DomainPlanPartitionOption[] {
  if (!overview) return [];
  const plans = selectedPlanId === undefined
    ? overview.plans
    : overview.plans.filter(plan => plan.id === selectedPlanId);

  const createNew = plans.map((plan): DomainPlanPartitionOption => ({
      key: `PARTITION:NEW:${plan.id}`,
      id: null,
      planId: plan.id,
      planCode: plan.planCode,
      planName: plan.planName,
      pointPrice: Number(plan.pointPrice),
      quotaMode: plan.quotaMode || 'TOTAL_DOMAIN',
      quotaLimit: plan.quotaLimit ?? null,
      quotaUsed: 0,
      label: `${plan.planName}（新）`,
      disabled: false,
      isNew: true,
      selection: {
        mode: 'NEW',
        planId: plan.id,
        periodCount: 1,
        autoRenew: plan.autoRenewDefault !== false,
        purchaseConfirmed: true
      }
    }));
  const existing = (overview.partitions || [])
    .filter(partition => plans.some(plan => plan.id === partition.planId))
    .sort((left, right) => left.purchaseSequence - right.purchaseSequence || left.id - right.id)
    .map((partition): DomainPlanPartitionOption => {
      const plan = plans.find(candidate => candidate.id === partition.planId)!;
      const unavailableReason = partitionUnavailableReason(
        partition,
        overview.serverNow || overview.serverTime
      );
      return {
        key: `PARTITION:EXISTING:${partition.id}`,
        id: partition.id,
        planId: plan.id,
        planCode: plan.planCode,
        planName: plan.planName,
        pointPrice: Number(plan.pointPrice),
        quotaMode: partition.quotaMode,
        quotaLimit: partition.quotaLimit,
        quotaUsed: partition.quotaUsed,
        label: `${partitionDisplayName(partition)}（${partitionQuotaLabel(partition)}${unavailableReason ? `，${unavailableReason}` : ''}）`,
        disabled: Boolean(unavailableReason),
        unavailableReason,
        isNew: false,
        selection: { mode: 'EXISTING', partitionId: partition.id },
        partition
      };
    });
  return [
    ...existing.filter(option => !option.disabled),
    ...createNew,
    ...existing.filter(option => option.disabled)
  ];
}

function normalizedDomain(value: string) {
  return value.trim().toLowerCase().replace(/^\*\./, '').replace(/\.$/, '');
}

function matchesAllocationKey(domain: string, allocationKey?: string | null) {
  if (!allocationKey) return false;
  const normalizedKey = normalizedDomain(allocationKey);
  return domain === normalizedKey || domain.endsWith(`.${normalizedKey}`);
}

function hasCapacity(partition: DomainPlanPartition, serverTime: string) {
  const available = partition.quotaLimit === null || partition.quotaUsed < partition.quotaLimit;
  return !partitionUnavailableReason(partition, serverTime) && available;
}

function partitionUnavailableReason(partition: DomainPlanPartition, serverTime: string) {
  const expiresAt = Date.parse(partition.expireAt);
  const now = Date.parse(serverTime);
  if (partition.status === 'ACTIVE'
    && Number.isFinite(expiresAt)
    && Number.isFinite(now)
    && expiresAt <= now) {
    return '已到期';
  }
  if (partition.status !== 'ACTIVE') return partitionStatusLabel(partition.status);
  if (partition.quotaLimit !== null && partition.quotaUsed >= partition.quotaLimit) {
    return '额度已满';
  }
  return '';
}

function quotaScope(mode: DomainPlanQuotaMode) {
  return mode === 'ROOT_DOMAIN' ? '根域名' : '单域名';
}

function quotaText(option: Pick<DomainPlanPartitionOption, 'quotaMode' | 'quotaLimit' | 'quotaUsed'>) {
  const scope = quotaScope(option.quotaMode);
  return option.quotaLimit === null
    ? `${scope} ${option.quotaUsed}/不限量`
    : `${scope} ${option.quotaUsed}/${option.quotaLimit}`;
}

export function buildDomainPlanPartitionRows(
  options: DomainPlanPartitionOption[],
  domain = '',
  overview?: DomainPlanPartitionOverview | null
): DomainPlanPartitionRow[] {
  return options.map(option => {
    const assignedRootPartition = domain && option.quotaMode === 'ROOT_DOMAIN' && !option.isNew
      ? assignedRootPartitionForDomain(domain, overview, option.planId)
      : null;
    const consumesQuota = !assignedRootPartition || assignedRootPartition.id !== option.partition?.id;
    const ignoresFullQuota = !consumesQuota && option.unavailableReason === '额度已满';
    const inheritedDisabled = ignoresFullQuota ? false : option.disabled;
    const inheritedUnavailableReason = ignoresFullQuota ? undefined : option.unavailableReason;
    const full = consumesQuota && option.quotaLimit !== null && option.quotaUsed >= option.quotaLimit;
    const warning = !full
      && consumesQuota
      && option.quotaLimit !== null
      && option.quotaUsed > 0
      && option.quotaUsed + 1 >= option.quotaLimit;
    const nextUsed = option.quotaLimit === null
      ? option.quotaUsed + (consumesQuota ? 1 : 0)
      : Math.min(option.quotaLimit, option.quotaUsed + (consumesQuota ? 1 : 0));
    const postSubmitQuotaLabel = option.quotaLimit === null
      ? `提交后将变为 ${quotaScope(option.quotaMode)} ${nextUsed}/不限量`
      : `提交后将变为 ${quotaScope(option.quotaMode)} ${nextUsed}/${option.quotaLimit}`;
    return {
      ...option,
      label: ignoresFullQuota ? option.label.replace('，额度已满）', '）') : option.label,
      disabled: inheritedDisabled || full,
      unavailableReason: full ? '额度已满' : inheritedUnavailableReason,
      quotaLabel: quotaText(option),
      quotaTone: full ? 'full' : warning ? 'warning' : inheritedDisabled ? 'muted' : 'available',
      postSubmitQuotaLabel
    };
  });
}

export function defaultPartitionForDomain(
  domain: string,
  overview: DomainPlanPartitionOverview | null | undefined,
  selectedPlanId?: number
): DomainPlanPartition | null {
  if (!overview) return null;
  const partitions = (overview.partitions || [])
    .filter(partition => selectedPlanId === undefined || partition.planId === selectedPlanId)
    .sort((left, right) => left.purchaseSequence - right.purchaseSequence || left.id - right.id);

  const assigned = assignedRootPartitionForDomain(domain, overview, selectedPlanId);
  if (assigned) return assigned;
  return partitions.find(partition => hasCapacity(partition, overview.serverNow || overview.serverTime)) || null;
}

export function assignedRootPartitionForDomain(
  domain: string,
  overview: DomainPlanPartitionOverview | null | undefined,
  selectedPlanId?: number
): DomainPlanPartition | null {
  if (!overview) return null;
  const normalized = normalizedDomain(domain);
  const partitions = (overview.partitions || [])
    .filter(partition => selectedPlanId === undefined || partition.planId === selectedPlanId);
  const rootedAllocation = overview.domains.find(item =>
    item.status !== 'DELETED'
    &&
    item.partitionId
    && matchesAllocationKey(normalized, item.allocationKey)
    && partitions.some(partition => partition.id === item.partitionId && partition.quotaMode === 'ROOT_DOMAIN')
  );
  return rootedAllocation
    ? partitions.find(partition => partition.id === rootedAllocation.partitionId) || null
    : null;
}

export function partitionStatusLabel(status: DomainPlanPartitionStatus) {
  if (status === 'ACTIVE') return '使用中';
  if (status === 'PAYMENT_PAUSED') return '欠费暂停';
  if (status === 'EXPIRED') return '已到期';
  return '已关闭';
}

export function quotaModeLabel(mode: DomainPlanQuotaMode) {
  if (mode === 'ROOT_DOMAIN') return '根域名额度';
  if (mode === 'UNLIMITED') return '不限域名';
  return '总域名额度';
}

export function suspensionReasonLabel(reason?: string | null) {
  const labels: Record<string, string> = {
    MANUAL_PAUSE: '用户手动暂停加速',
    PARTITION_INSUFFICIENT: '自动续费时积分余额不足',
    PARTITION_AUTO_RENEW_OFF: '套餐分区到期且未开启自动续费',
    PARTITION_MANUAL_RESUME: '续费已完成，等待手动启用加速'
  };
  if (!reason) return '';
  return labels[reason] || '套餐当前处于暂停状态，请刷新后查看或联系我们';
}
